import React, { useState, useEffect, useRef } from 'react'
import { useJobDescriptions } from '../contexts/JobDescriptionContext'
import { useToast } from '../contexts/ToastContext'
import { apiClient } from '../utils/api'

export function JobDescriptionUpload() {
  const { showSuccess, showError } = useToast();
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showEditDropdown, setShowEditDropdown] = useState(false)
  const { jobDescriptions, addJobDescription, updateJobDescription } = useJobDescriptions()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const clearForm = () => {
    setTitle('')
    setDescription('')
    setEditingId(null)
  }

  const saveJobDescription = async () => {
    if (!title.trim() || !description.trim()) {
      showError('Please fill in both title and description')
      return
    }

    setSaving(true)
    try {
      if (editingId) {
        // Update existing job description
        const result = await apiClient.put<{ success: boolean; data: any; message: string }>(`/job-descriptions/${editingId}`, {
          title: title.trim(),
          description: description.trim()
        })

        console.log('Job description updated:', result)
        showSuccess('Job description updated successfully!')
        updateJobDescription(editingId, result.data)
      } else {
        // Create new job description
        const result = await apiClient.post<{ success: boolean; data: any; message: string }>('/job-descriptions', {
          title: title.trim(),
          description: description.trim()
        })

        console.log('Job description saved:', result)
        showSuccess('Job description saved successfully!')
        addJobDescription(result.data)
      }
      
      clearForm()
    } catch (error) {
      console.error('Save error:', error)
      showError(error instanceof Error ? error.message : 'Failed to save job description')
    } finally {
      setSaving(false)
    }
  }

  const selectJobDescriptionForEdit = (jd: any) => {
    setTitle(jd.title)
    setDescription(jd.description)
    setEditingId(jd.id)
    setShowEditDropdown(false)
  }

  const handleCancel = () => {
    clearForm()
    setShowEditDropdown(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowEditDropdown(false)
      }
    }

    if (showEditDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEditDropdown])

  return (
    <div className="border border-base-300 p-4 rounded-lg">
      {/* Header with title and edit button */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-semibold text-base-content mb-2">
            {editingId ? 'Edit Job Role' : 'Add or Edit Job Role'}
          </h2>
          <p className="text-base-content/70">
            This information will be used to analyze resumes. Include specifics of what you expect from the best fit candidates for this role.
          </p>
        </div>
        
        {/* Edit/Cancel button */}
        <div className="relative" ref={dropdownRef}>
          {editingId ? (
            <button 
              className="btn btn-outline btn-sm w-32"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          ) : (
            <button 
              className="btn btn-outline btn-sm w-32"
              onClick={() => setShowEditDropdown(!showEditDropdown)}
              disabled={jobDescriptions.length === 0 || saving}
            >
              Edit Job Role
            </button>
          )}
          
          {/* Dropdown for existing job descriptions */}
          {showEditDropdown && jobDescriptions.length > 0 && (
            <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
              {jobDescriptions.map((jd) => (
                <button
                  key={jd.id}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
                  onClick={() => selectJobDescriptionForEdit(jd)}
                >
                  <div className="font-medium text-sm text-gray-900 truncate">{jd.title}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Title Input */}
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text font-medium text-base-content">Job Title</span>
        </label>
        <input 
          type="text"
          placeholder="e.g., Senior Software Engineer"
          className="input input-bordered w-full bg-base-200 border-base-300 text-base-content"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
        />
      </div>

      {/* Description Textarea */}
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text font-medium text-base-content">Job Description</span>
        </label>
        <textarea 
          className="textarea textarea-bordered w-full h-40 bg-base-200 border-base-300 text-base-content"
          placeholder="Paste the complete job description here..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
        />
        <label className="label">
          <span className="label-text-alt text-base-content/60">
            {description.length} characters
          </span>
        </label>
      </div>

      {/* Save Button */}
      <button 
        className={`btn btn-primary w-full ${saving ? 'loading' : ''}`}
        onClick={saveJobDescription} 
        disabled={saving || !title.trim() || !description.trim()}
      >
        {saving ? (
          <>
            <span className="loading loading-spinner loading-sm"></span>
            {editingId ? 'Updating...' : 'Saving...'}
          </>
        ) : (
          editingId ? 'Update Job Description' : 'Save Job Description'
        )}
      </button>
    </div>
  )
} 